import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, canCreateCylinder } from "@/contexts/AuthContext";

const MOUNTING = ["Flange", "Clevis", "Trunnion", "Foot", "Rod-Eye", "Pivot"];
const SEALS = ["Standard NBR", "Viton HT", "PTFE", "Polyurethane", "PU-High Load"];

export default function CreateCylinder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [pos, setPos] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    customer_id: "",
    plant_id: "",
    // ALL commercial linkage below is OPTIONAL. HISPL now allows a manual
    // 5-digit Cylinder to be created against just Customer + Plant + Number.
    purchase_order_id: "",
    cylinder_number: "",  // Manual 5-digit — MANDATORY when this form is used standalone.
    dc_number: "",
    quantity: 1,
    quoted_price: "",
    planned_dispatch_date: "",
    serial_number_prefix: "",
    spec: {
      bore_diameter_mm: "",
      rod_diameter_mm: "",
      stroke_length_mm: "",
      closed_length_mm: "",
      operating_pressure_bar: "",
      mounting_type: "Flange",
      seal_kit_type: "Standard NBR",
      custom_notes: "",
    },
  });

  useEffect(() => {
    api.get("/customers")
      .then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setCustomers([]));
  }, []);

  useEffect(() => {
    if (!form.customer_id) { setPlants([]); setPos([]); return; }
    api.get(`/plants?customer_id=${form.customer_id}`)
      .then((r) => setPlants(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setPlants([]));
    api.get(`/purchase-orders?customer_id=${form.customer_id}`)
      .then((r) => setPos(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setPos([]));
  }, [form.customer_id]);

  if (!canCreateCylinder(user?.role)) {
    return (
      <Card className="max-w-xl mx-auto mt-12 border-red-200">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900">Access denied</h2>
          <p className="text-sm text-slate-500 mt-2">
            Only Admin and Super Admin can access this development-only screen.
          </p>
        </CardContent>
      </Card>
    );
  }

  async function submit(e) {
    e.preventDefault();
    // ONLY Customer + Plant + 5-digit Cylinder Number are required now.
    if (!form.customer_id || !form.plant_id) {
      toast.error("Customer and Place/Location are required");
      return;
    }
    if (!form.cylinder_number || !/^\d{5}$/.test(form.cylinder_number)) {
      toast.error("Cylinder Number must contain exactly 5 digits.");
      return;
    }
    setSaving(true);
    try {
      // Build payload with ONLY fields that have values — everything optional
      // stays absent so the backend uses its null-tolerant defaults.
      const specHasAny = Object.entries(form.spec).some(([k, v]) =>
        !["mounting_type", "seal_kit_type", "custom_notes"].includes(k) && String(v).trim() !== "");
      const payload = {
        customer_id: form.customer_id,
        plant_id: form.plant_id,
        cylinder_number: form.cylinder_number,
        quantity: 1,  // Manual entries are always exactly 1
      };
      if (form.purchase_order_id) payload.purchase_order_id = form.purchase_order_id;
      if (form.dc_number.trim()) payload.dc_number = form.dc_number.trim();
      if (form.planned_dispatch_date) payload.planned_dispatch_date = form.planned_dispatch_date;
      if (form.serial_number_prefix.trim()) payload.serial_number_prefix = form.serial_number_prefix.trim();
      if (String(form.quoted_price).trim() !== "") payload.quoted_price = parseFloat(form.quoted_price);
      if (specHasAny) {
        payload.spec = {
          bore_diameter_mm: parseFloat(form.spec.bore_diameter_mm) || 0,
          rod_diameter_mm: parseFloat(form.spec.rod_diameter_mm) || 0,
          stroke_length_mm: parseFloat(form.spec.stroke_length_mm) || 0,
          closed_length_mm: parseFloat(form.spec.closed_length_mm) || 0,
          operating_pressure_bar: parseFloat(form.spec.operating_pressure_bar) || 0,
          mounting_type: form.spec.mounting_type,
          seal_kit_type: form.spec.seal_kit_type,
          custom_notes: form.spec.custom_notes || null,
        };
      }
      const res = await api.post("/cylinders", payload);
      const first = (res.data?.cylinders || res.data?.created || [])[0];
      toast.success(`Cylinder ${first?.cylinder_number || form.cylinder_number} created`);
      if (first?.id) nav(`/cylinders/${first.id}`);
      else nav("/cylinders");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div data-testid="create-cylinder-page" className="max-w-4xl">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-amber-600 font-semibold flex items-center gap-1.5">
          <span className="inline-flex w-1.5 h-1.5 rounded-full bg-amber-500" />
          Dev Mode · Manual Creation
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>
          Create Cylinder
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Temporary Admin-only screen for demo purposes. Will be disabled once the Order Acceptance workflow (Module 2)
          is available. In production, cylinder numbers are generated only from confirmed OA lines.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        {/* Lineage */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Cylinder</div>
            <div className="text-xs text-slate-500">Only Customer + Place + 5-digit Cylinder Number are required. Everything below is optional.</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, plant_id: "", purchase_order_id: "" })}>
                <SelectTrigger data-testid="cust-select" className="mt-1"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Place / Location *</Label>
              <Select value={form.plant_id} onValueChange={(v) => setForm({ ...form, plant_id: v })} disabled={!form.customer_id}>
                <SelectTrigger data-testid="plant-select" className="mt-1"><SelectValue placeholder="Select plant" /></SelectTrigger>
                <SelectContent>
                  {plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Cylinder Number *
                <span className="ml-1 text-slate-400 font-normal normal-case">(manual · 5 digits · unique)</span>
              </Label>
              <Input
                data-testid="cyl-num-input"
                value={form.cylinder_number}
                onChange={(e) => setForm({ ...form, cylinder_number: e.target.value.replace(/[^0-9]/g, "").slice(0, 5) })}
                placeholder="e.g. 01234"
                maxLength={5}
                className="mt-1 font-mono text-lg"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Customer PO <span className="text-slate-400 font-normal normal-case">(optional)</span></Label>
              <Select value={form.purchase_order_id || "__none__"}
                      onValueChange={(v) => setForm({ ...form, purchase_order_id: v === "__none__" ? "" : v })}
                      disabled={!form.customer_id}>
                <SelectTrigger data-testid="po-select" className="mt-1"><SelectValue placeholder="— Not Linked —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not Linked —</SelectItem>
                  {pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">DC Number <span className="text-slate-400 font-normal normal-case">(optional · reconditioning)</span></Label>
              <Input
                data-testid="dc-input"
                value={form.dc_number}
                onChange={(e) => setForm({ ...form, dc_number: e.target.value })}
                placeholder="Leave blank if not applicable"
                className="mt-1 font-mono"
              />
            </div>
          </div>
        </Card>

        {/* Order details */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Order Details</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Quantity (1-50)</Label>
              <Input type="number" min="1" max="50" data-testid="qty-input" value={form.quantity}
                     onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Quoted Price (₹)</Label>
              <Input type="number" min="0" data-testid="price-input" value={form.quoted_price}
                     onChange={(e) => setForm({ ...form, quoted_price: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Planned Dispatch</Label>
              <Input type="date" data-testid="dispatch-input" value={form.planned_dispatch_date}
                     onChange={(e) => setForm({ ...form, planned_dispatch_date: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Serial Prefix</Label>
              <Input data-testid="serial-prefix-input" value={form.serial_number_prefix}
                     onChange={(e) => setForm({ ...form, serial_number_prefix: e.target.value })}
                     className="mt-1 font-mono" placeholder="e.g. JSW-VJN" />
            </div>
          </div>
        </Card>

        {/* Specification */}
        <Card className="border-slate-200">
          <div className="px-5 py-3 border-b border-slate-100">
            <div className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>Specification</div>
            <div className="text-xs text-slate-500">Snapshot values applied to every cylinder in this batch</div>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["bore_diameter_mm", "Bore Ø (mm)"],
              ["rod_diameter_mm", "Rod Ø (mm)"],
              ["stroke_length_mm", "Stroke Length (mm)"],
              ["closed_length_mm", "Closed Length (mm)"],
              ["operating_pressure_bar", "Operating Pressure (bar)"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-wider text-slate-600">{l}</Label>
                <Input type="number" step="0.1" data-testid={`spec-${k}`}
                       value={form.spec[k]}
                       onChange={(e) => setForm({ ...form, spec: { ...form.spec, [k]: e.target.value } })}
                       className="mt-1 font-mono" />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Mounting Type</Label>
              <Select value={form.spec.mounting_type}
                      onValueChange={(v) => setForm({ ...form, spec: { ...form.spec, mounting_type: v } })}>
                <SelectTrigger data-testid="mounting-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MOUNTING.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Seal Kit</Label>
              <Select value={form.spec.seal_kit_type}
                      onValueChange={(v) => setForm({ ...form, spec: { ...form.spec, seal_kit_type: v } })}>
                <SelectTrigger data-testid="seal-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SEALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Custom Notes</Label>
              <Textarea data-testid="notes-input" value={form.spec.custom_notes}
                        onChange={(e) => setForm({ ...form, spec: { ...form.spec, custom_notes: e.target.value } })}
                        rows={2} className="mt-1"
                        placeholder="Non-standard requirements, special coatings, etc." />
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => nav("/cylinders")}>Cancel</Button>
          <Button type="submit" data-testid="submit-create" disabled={saving}
                  className="bg-blue-900 hover:bg-blue-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Cylinder(s)"}
          </Button>
        </div>
      </form>
    </div>
  );
}
